import React from 'react';

import { useTranslations, Letters } from 'settings/i18n';
import { useIsRtl } from 'hooks/useCurrentLocale';

import { Wrapper } from 'components/EducationSection/EducationSection';
import Text from 'components/Text';
import TransparentButton from 'components/Button/TransparentButton';

import {
  Glossary,
  GlossaryHeading,
  AlphabetList,
  AlphabetListItem,
  Letter,
  SearchInput,
  GlossaryContent,
  PhraseTitle,
  SearchInputWrapper,
  SearchIcon,
} from './styled';

type Phrase = { title: string; content: string };

function extractTitle(title: Phrase['title']) {
  const result = title.split('|');

  if (result.length > 1) {
    return result[1];
  }

  return title;
}

function doesPhraseStartingWithLetterExist(
  letter: string,
  phrases: Phrase[],
): boolean {
  return phrases.some((phrase) =>
    phrase.title.toLowerCase().startsWith(letter.toLowerCase()),
  );
}

const EducationGlossary = ({
  letters,
}: {
  letters: Letters['translation']['letters'];
}) => {
  const t = useTranslations();

  const isRtl = useIsRtl();

  const phrases: Phrase[] = [
    { title: 'airdropTitle', content: 'airdropContent' },
    { title: 'alphanumericTitle', content: 'alphanumericContent' },
    { title: 'altcoinsTitle', content: 'altcoinsContent' },
    {
      title: 'antiMoneyLaunderingAmlTitle',
      content: 'antiMoneyLaunderingAmlContent',
    },
    { title: 'asicMinersTitle', content: 'asicMinersContent' },
    { title: 'assetTitle', content: 'assetContent' },
    { title: 'backupFileTitle', content: 'backupFileContent' },
    { title: 'bitcoinTitle', content: 'bitcoinContent' },
    { title: 'blockExplorerTitle', content: 'blockExplorerContent' },
    { title: 'blockRewardTitle', content: 'blockRewardContent' },
    { title: 'blockchainNetworkTitle', content: 'blockchainNetworkContent' },
    {
      title: 'blockchainTransactionTitle',
      content: 'blockchainTransactionContent',
    },
    { title: 'blockchainTitle', content: 'blockchainContent' },
    { title: 'buyOrderTitle', content: 'buyOrderContent' },
    { title: 'cloudMiningTitle', content: 'cloudMiningContent' },
    { title: 'coinToCoinTradingTitle', content: 'coinToCoinTradingContent' },
    { title: 'coldWalletTitle', content: 'coldWalletContent' },
    { title: 'cryptoAssetTitle', content: 'cryptoAssetContent' },
    { title: 'cryptoTradingTitle', content: 'cryptoTradingContent' },
    {
      title: 'cryptoWalletAddressBlockchainAddressPublicKeyPublicAddressTitle',
      content:
        'cryptoWalletAddressBlockchainAddressPublicKeyPublicAddressContent',
    },
    {
      title: 'cryptoWalletTitle',
      content: 'cryptoWalletContent',
    },
    { title: 'cryptoAtmTitle', content: 'cryptoAtmContent' },
    {
      title: 'cryptocurrencyFluctuationTitle',
      content: 'cryptocurrencyFluctuationContent',
    },
    { title: 'cryptocurrencyTitle', content: 'cryptocurrencyContent' },
    {
      title: 'cryptocurrencyExchangeTitle',
      content: 'cryptocurrencyExchangeContent',
    },
    {
      title: 'cryptographicAlgorithmTitle',
      content: 'cryptographicAlgorithmContent',
    },
    { title: 'cryptographicCodeTitle', content: 'cryptographicCodeContent' },
    { title: 'cryptographyTitle', content: 'cryptographyContent' },
    { title: 'cryptoMiningTitle', content: 'cryptoMiningContent' },
    { title: 'dataBlockTitle', content: 'dataBlockContent' },
    { title: 'debitcreditCardTitle', content: 'debitCreditCardContent' },
    {
      title: 'decentralizedApplicationsDappsTitle',
      content: 'decentralizedApplicationsDappsContent',
    },
    { title: 'decentralizedTitle', content: 'decentralizedContent' },
    { title: 'desktopWalletTitle', content: 'desktopWalletContent' },
    { title: 'digitalAssetTitle', content: 'digitalAssetContent' },
    { title: 'ethereumTitle', content: 'ethereumContent' },
    { title: 'fiatCurrencymoneyTitle', content: 'fiatCurrencymoneyContent' },
    {
      title: 'fomoFearOfMissingOutTitle',
      content: 'fomoFearOfMissingOutContent',
    },
    { title: 'genesisBlockTitle', content: 'genesisBlockContent' },
    { title: 'hackerTitle', content: 'hackerContent' },
    { title: 'halvingTitle', content: 'halvingContent' },
    { title: 'hashPowerTitle', content: 'hashPowerContent' },
    { title: 'hashRateTitle', content: 'hashRateContent' },
    { title: 'hashValueTitle', content: 'hashValueContent' },
    { title: 'hotWalletTitle', content: 'hotWalletContent' },
    { title: 'immutableDataTitle', content: 'immutableDataContent' },
    {
      title: 'initialCoinOfferingIcoTitle',
      content: 'initialCoinOfferingIcoContent',
    },
    { title: 'irreversibleTitle', content: 'irreversibleContent' },
    { title: 'keyloggerTitle', content: 'keyloggerContent' },
    {
      title: 'knowYourCustomerKycTitle',
      content: 'knowYourCustomerKycContent',
    },
    { title: 'ledgerTitle', content: 'ledgerContent' },
    {
      title: 'ledgerHardwareWalletTitle',
      content: 'ledgerHardwareWalletContent',
    },
    {
      title: 'marketCapitalizationTitle',
      content: 'marketCapitalizationContent',
    },
    { title: 'mempoolTitle', content: 'mempoolContent' },
    { title: 'minersTitle', content: 'minersContent' },
    { title: 'miningFarmTitle', content: 'miningFarmContent' },
    { title: 'miningPoolTitle', content: 'miningPoolContent' },
    { title: 'miningRewardsTitle', content: 'miningRewardsContent' },
    { title: 'mobileWalletTitle', content: 'mobileWalletContent' },
    { title: 'nodesTitle', content: 'nodesContent' },
    { title: 'nonceTitle', content: 'nonceContent' },
    { title: 'orderBookTitle', content: 'orderBookContent' },
    { title: 'paperWalletTitle', content: 'paperWalletContent' },
    { title: 'proofOfStakePosTitle', content: 'proofOfStakePosContent' },
    { title: 'proofOfWorkPowTitle', content: 'proofOfWorkPowContent' },
    { title: 'privateBlockchainTitle', content: 'privateBlockchainContent' },
    { title: 'privateKeyaddressTitle', content: 'privateKeyaddressContent' },
    { title: 'publicBlockchainTitle', content: 'publicBlockchainContent' },
    {
      title: 'publicLedgerdecentralizedLedgerTitle',
      content: 'publicLedgerdecentralizedLedgerContent',
    },
    { title: 'recipientTitle', content: 'recipientContent' },
    { title: 'rewardReductionTitle', content: 'rewardReductionContent' },
    { title: 'satoshiTitle', content: 'satoshiContent' },
    { title: 'satoshiNakamotoTitle', content: 'satoshiNakamotoContent' },
    { title: 'satoshiSendTestTitle', content: 'satoshiSendTestContent' },
    { title: 'seedPhraseTitle', content: 'seedPhraseContent' },
    { title: 'sellOrderTitle', content: 'sellOrderContent' },
    { title: 'senderTitle', content: 'senderContent' },
    { title: 'smartContractTitle', content: 'smartContractContent' },
    { title: 'synchronizedCopyTitle', content: 'synchronizedCopyContent' },
    { title: 'tradeCommissionsTitle', content: 'tradeCommissionsContent' },
    { title: 'tradingEngineTitle', content: 'tradingEngineContent' },
    { title: 'tradingPairTitle', content: 'tradingPairContent' },
    { title: 'transactionTitle', content: 'transactionContent' },
    {
      title: 'transactionAcceleratorTitle',
      content: 'transactionAcceleratorContent',
    },
    { title: 'transactionFeesTitle', content: 'transactionFeesContent' },
    {
      title: 'transactiontradingVolumeTitle',
      content: 'transactiontradingVolumeContent',
    },
    {
      title: 'trezorHardwareWalletTitle',
      content: 'trezorHardwareWalletContent',
    },
    {
      title: 'twoFactorAuthentication2faTitle',
      content: 'twoFactorAuthentication2faContent',
    },
    {
      title: 'unconfirmedTransactionTitle',
      content: 'unconfirmedTransactionContent',
    },
    { title: 'whitePaperTitle', content: 'whitePaperContent' },
  ].map((phrase) => ({
    title: t(phrase.title),
    content: t(phrase.content),
  }));

  const [activeLetter, setActiveLetter] = React.useState<null | string>(
    letters.split(',')[0],
  );
  const [searchPhrase, setSearchPhrase] = React.useState<string>();

  const filteredPhrases = phrases
    .filter((phrase) => {
      if (activeLetter)
        return phrase.title.toLowerCase().startsWith(activeLetter);
      if (searchPhrase === '' || !searchPhrase) return false;
      return true;
    })
    .filter((phrase) =>
      searchPhrase === '' || !searchPhrase
        ? true
        : extractTitle(phrase.title)
            .toLowerCase()
            .includes(searchPhrase.toLowerCase()),
    );

  return (
    <Glossary>
      <Wrapper>
        <GlossaryHeading
          isRtl={isRtl}
          as="h2"
          size="heading"
          color="dark"
          weight="medium"
          letterSpacing="normal">
          {t('glossary')}
        </GlossaryHeading>

        <AlphabetList>
          {letters
            .split(',')
            .map((letter) => letter.toLowerCase())
            .map((letter) => (
              <AlphabetListItem key={letter}>
                <Letter
                  as={TransparentButton}
                  disabled={!doesPhraseStartingWithLetterExist(letter, phrases)}
                  active={activeLetter === letter}
                  onClick={() => {
                    setActiveLetter(activeLetter === letter ? null : letter);
                    setSearchPhrase('');
                  }}>
                  {letter}
                </Letter>
              </AlphabetListItem>
            ))}
        </AlphabetList>

        <SearchInputWrapper>
          <SearchInput
            type="search"
            value={searchPhrase}
            onChange={(event) => setSearchPhrase(event.target.value)}
          />
          <SearchIcon />
        </SearchInputWrapper>

        <GlossaryContent>
          {filteredPhrases
            .sort((a, b) =>
              extractTitle(a.title)
                .toLocaleLowerCase()
                .localeCompare(extractTitle(b.title).toLocaleLowerCase()),
            )
            .map(({ title, content }) => (
              <li key={title + content}>
                <PhraseTitle isRtl={isRtl} as="h3">
                  {extractTitle(title)}
                </PhraseTitle>
                <Text isRtl={isRtl}>{content}</Text>
              </li>
            ))}
        </GlossaryContent>
      </Wrapper>
    </Glossary>
  );
};

export default EducationGlossary;
